import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Typography,
  Paper,
  Grid,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import { Visibility, VisibilityOff, PersonAdd } from '@mui/icons-material';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import Swal from 'sweetalert2';

const initialUser = {
  username: '',
  password: '',
  email: '',
  role: 'moderador',
};

const initialEmployee = {
  employeeFirstName: '',
  employeeFirstLastName: '',
  employeeSecondLastName: '',
  employeePosition: '',
  hiredStatus: 'active',
  employeeCellphone: '',
  employeePhone: '',
  address: '',
};

const initialContact = {
  contactName: '',
  relationship: '',
  phone: '',
  email: '',
  address: '',
  notes: '',
};

import { ContactEmergency, ExpandMore, ExpandLess } from '@mui/icons-material';
import Collapse from '@mui/material/Collapse';

export default function CreateUsers() {
  const [user, setUser] = useState(initialUser);
  const [employee, setEmployee] = useState(initialEmployee);
  const [contact, setContact] = useState(initialContact);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showContact, setShowContact] = useState(false);

  const handleUserChange = (e) => {
    setUser({ ...user, [e.target.name]: e.target.value });
  };

  const handleEmployeeChange = (e) => {
    setEmployee({ ...employee, [e.target.name]: e.target.value });
  };

  const handleContactChange = (e) => {
    setContact({ ...contact, [e.target.name]: e.target.value });
  };

  const validate = () => {
    if (!user.email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(user.email)) return 'Email inválido.';
    if (!user.role) return 'El rol es requerido.';
    if (!employee.employeeFirstName) return 'El nombre del empleado es requerido.';
    if (!employee.employeeFirstLastName) return 'El apellido del empleado es requerido.';
    if (!employee.employeePosition) return 'El puesto es requerido.';
    if (!employee.hiredStatus) return 'El estado es requerido.';
    if (!employee.employeeCellphone && !employee.employeePhone) return 'Debe ingresar al menos un teléfono del empleado.';
    if (employee.employeeCellphone && !/^[0-9+\s()-]{7,20}$/.test(employee.employeeCellphone)) return 'Celular del empleado inválido.';
    if (employee.employeePhone && !/^[0-9+\s()-]{7,20}$/.test(employee.employeePhone)) return 'Teléfono del empleado inválido.';
    if (employee.address && employee.address.length > 250) return 'La dirección del empleado es demasiado larga.';
    // Validar contacto de emergencia solo si algún campo está lleno
    const anyContact = Object.values(contact).some(val => val && val.trim() !== '');
    if (anyContact) {
      if (!contact.contactName) return 'El nombre del contacto de emergencia es requerido.';
      if (!contact.relationship) return 'La relación del contacto de emergencia es requerida.';
      if (!contact.phone) return 'El teléfono del contacto de emergencia es requerido.';
      if (contact.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(contact.email)) return 'Email del contacto de emergencia inválido.';
      if (contact.address && contact.address.length > 200) return 'La dirección del contacto de emergencia es demasiado larga.';
      if (contact.notes && contact.notes.length > 255) return 'Las notas del contacto de emergencia son demasiado largas.';
    }
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    const validation = validate();
    if (validation) {
      setError(validation);
      return;
    }
    setLoading(true);
    try {
      // Obtener el primer nombre antes del primer espacio
      const employeeName = employee.employeeFirstName
      const employeeFirstName = employee.employeeFirstName.split(' ')[0]
      const employeeFirstLastName = employee.employeeFirstLastName.trim()
      const employeeSecondLastName = employee.employeeSecondLastName.trimEnd()
      const updatedEmployee = { ...employee, employeeName: `${employeeName} ${employeeFirstLastName} ${employeeSecondLastName}` };

      const username = `${employeeFirstName}.${employeeFirstLastName}`.toLowerCase();
      // Variable del año actual
      const currentYear = new Date().getFullYear();
      const password = `${employeeFirstName}.${employeeFirstLastName}-${currentYear}`.toLowerCase();
      const updatedUser = { ...user, username: username, password: password };

      const anyContact = Object.values(contact).some(val => val && val.trim() !== '');
      const payload = anyContact ? { user: updatedUser, employee: updatedEmployee, contact } : { user: updatedUser, employee: updatedEmployee };
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No se encontró el token de autenticación.');
        setLoading(false);
        return;
      }

      const confirmation = await Swal.fire({
        title: 'Confirmar Registro',
        text: '¿Está seguro de que desea crear este usuario y empleado?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, crear',
        confirmButtonColor: '#8630d6ff',
        cancelButtonColor: '#d33',
        cancelButtonText: 'Cancelar'
      });

      if (!confirmation.isConfirmed) {
        setLoading(false);
        return;
      }

      // Mostrar swal de carga
      Swal.fire({
        title: 'Registrando usuario...',
        text: 'Por favor espere mientras se procesa la solicitud.',
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const response = await fetch('https://dprincess-training.servehttp.com/users/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error data:', errorData.error);
        setError(errorData.error || 'Error al crear usuario/empleado/contacto.');
        setLoading(false);
        Swal.close();
        throw new Error(errorData.error || 'Error al crear usuario/empleado/contacto.');
      }

      const data = await response.json();

      setSuccess(data.message || 'Usuario y empleado creados exitosamente.');
      setUser(initialUser);
      setEmployee(initialEmployee);
      setContact(initialContact);
      setShowContact(false);
      setLoading(false);
      Swal.close();
      await Swal.fire({
        title: '¡Registro exitoso!',
        text: data.message || 'Usuario y empleado creados exitosamente.',
        icon: 'success',
        confirmButtonColor: '#8630d6ff',
      });
    } catch (err) {
      console.error('Error:', err);
      setError(err.message || 'Error al crear usuario/empleado/contacto.');
      setLoading(false);
      Swal.close();
    }
  };

  return (
    <Box sx={{ maxWidth: 500, mx: 'auto', mt: 4, mb: 4 }}>
      <Paper elevation={4} sx={{ p: 4, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
          <PersonAdd color="primary" />
          <Typography variant="h5" fontWeight={700} color="primary.main">
            Crear Usuario y Empleado
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Complete la información para registrar un nuevo usuario y su empleado asociado.
        </Typography>
        <form onSubmit={handleSubmit} autoComplete="off">
          <Grid container spacing={2}>
            <Grid item xs={12} mt={2}>
              <Typography variant="subtitle1" fontWeight={600} color="primary.main" mb={1}>
                Datos del Empleado
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Nombre (sin apellidos)"
                name="employeeFirstName"
                value={employee.employeeFirstName}
                onChange={handleEmployeeChange}
                fullWidth
                required
                inputProps={{ maxLength: 100 }}
              />
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Primer apellido"
                    placeholder='Apellido del padre'
                    name="employeeFirstLastName"
                    value={employee.employeeFirstLastName}
                    onChange={handleEmployeeChange}
                    fullWidth
                    required
                    inputProps={{ maxLength: 100 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Segundo apellido"
                    placeholder='Apellido de la madre'
                    name="employeeSecondLastName"
                    value={employee.employeeSecondLastName}
                    onChange={handleEmployeeChange}
                    fullWidth
                    inputProps={{ maxLength: 100 }}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Email"
                name="email"
                value={user.email}
                onChange={handleUserChange}
                fullWidth
                required
                inputProps={{ maxLength: 100 }}
                type="email"
                autoComplete='email'
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Puesto"
                name="employeePosition"
                value={employee.employeePosition}
                onChange={handleEmployeeChange}
                fullWidth
                required
                inputProps={{ maxLength: 100 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                label="Rol"
                name="role"
                value={user.role}
                onChange={handleUserChange}
                fullWidth
                required
              >
                <MenuItem value="moderador">Moderador (Visualización y creación controlada)</MenuItem>
                <MenuItem value="admin">Administrador (Permiso total)</MenuItem>
                <MenuItem value="viewer">Espectador (Permiso de visualización muy limitado)</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Celular"
                name="employeeCellphone"
                value={employee.employeeCellphone}
                onChange={handleEmployeeChange}
                fullWidth
                required
                inputProps={{ maxLength: 20 }}
                placeholder="Ej: 809-000-0000"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Teléfono"
                name="employeePhone"
                value={employee.employeePhone}
                onChange={handleEmployeeChange}
                fullWidth
                inputProps={{ maxLength: 20 }}
                placeholder="Ej: 809-000-0000"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Dirección (opcional)"
                name="address"
                value={employee.address}
                onChange={handleEmployeeChange}
                fullWidth
                inputProps={{ maxLength: 250 }}
                multiline
                minRows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                label="Estado"
                name="hiredStatus"
                value={employee.hiredStatus}
                onChange={handleEmployeeChange}
                fullWidth
                required
              >
                <MenuItem value="active">Activo</MenuItem>
                <MenuItem value="inactive">Inactivo</MenuItem>
              </TextField>
            </Grid>
            {/* Contacto de emergencia opcional */}
            <Grid item xs={12} mt={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }} onClick={() => setShowContact((s) => !s)}>
                <ContactEmergency color="secondary" />
                <Typography variant="subtitle1" fontWeight={600} color="secondary.main">
                  Contacto de emergencia (opcional)
                </Typography>
                <IconButton size="small" sx={{ ml: 1 }}>
                  {showContact ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Collapse in={showContact}>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Nombre (Requerido)"
                      name="contactName"
                      value={contact.contactName}
                      onChange={handleContactChange}
                      fullWidth
                      inputProps={{ maxLength: 100 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel sx={{
                        '&.Mui-focused': {
                          color: '#dc2626'
                        }
                      }}>
                        Relación
                      </InputLabel>
                      <Select
                        value={contact.relationship}
                        label="Relación (Requerido)"
                        name='relationship'
                        onChange={handleContactChange}
                        startAdornment={
                          <InputAdornment position="start">
                            <FamilyRestroomIcon color="action" />
                          </InputAdornment>
                        }
                        sx={{
                          borderRadius: 2,
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#dc2626',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#dc2626',
                          },
                        }}
                      >
                        <MenuItem value="Padre">Padre</MenuItem>
                        <MenuItem value="Madre">Madre</MenuItem>
                        <MenuItem value="Esposo/a">Esposo/a</MenuItem>
                        <MenuItem value="Hermano/a">Hermano/a</MenuItem>
                        <MenuItem value="Hijo/a">Hijo/a</MenuItem>
                        <MenuItem value="Abuelo/a">Abuelo/a</MenuItem>
                        <MenuItem value="Tío/a">Tío/a</MenuItem>
                        <MenuItem value="Primo/a">Primo/a</MenuItem>
                        <MenuItem value="Amigo/a">Amigo/a</MenuItem>
                        <MenuItem value="Pareja">Pareja</MenuItem>
                        <MenuItem value="Compañero de trabajo">Compañero de trabajo</MenuItem>
                        <MenuItem value="Vecino/a">Vecino/a</MenuItem>
                        <MenuItem value="Otro">Otro</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Celular (Requerido)"
                      name="phone"
                      value={contact.phone}
                      onChange={handleContactChange}
                      fullWidth
                      inputProps={{ maxLength: 20 }}
                      placeholder="Ej: 809-000-0000"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Email"
                      name="email"
                      value={contact.email}
                      onChange={handleContactChange}
                      fullWidth
                      inputProps={{ maxLength: 100 }}
                      type="email"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Dirección"
                      name="address"
                      value={contact.address}
                      onChange={handleContactChange}
                      fullWidth
                      inputProps={{ maxLength: 200 }}
                      placeholder="Ej: Calle Falsa 123"
                      helperText="Máximo 200 caracteres"
                      multiline
                      minRows={2}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Notas"
                      name="notes"
                      placeholder="Información adicional relevante sobre el contacto de emergencia (horarios disponibles, instrucciones especiales, etc.)"
                      helperText="Máximo 255 caracteres"
                      value={contact.notes}
                      onChange={handleContactChange}
                      fullWidth
                      inputProps={{ maxLength: 255 }}
                      multiline
                      minRows={2}
                    />
                  </Grid>
                </Grid>
              </Collapse>
            </Grid>
            <Grid item xs={12} mt={2}>
              {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
              {success && <Alert severity="success" sx={{ mb: 1 }}>{success}</Alert>}
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={22} color="inherit" /> : <PersonAdd />}
                sx={{ mt: 1, fontWeight: 600 }}
              >
                Registrar empleado
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
}
